import { useIntl } from 'react-intl';

import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import {
	findAllMeasurementsForFerryTrip,
	getMeasurementLabelFromId,
	getMeasurementValueFromId,
} from 'Util/_HumanWritten/MeasurementUtils';
import { formatWeight, parseWeightUnit } from 'Util/Weight';
import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { withLinkedCharacteristics } from 'Modules/Cargo';
import CargoCharacteristicTags from 'Views/Components/_HumanWritten/Tag/CargoCharacteristicTags';
import { BookingDetailSection, SectionProps } from '../../SharedSections/BookingDetailSection';
import { DetailLine, getWidthLabelFromId } from './VehicleInfoSection';

export function TrailerInfoSection({ booking, canEdit, onEdit, isInvoiced }: SectionProps) {
	const intl = useIntl();

	const towOnInfo = booking.bookedSummary?.towOnInfo;

	// Only show this section when the booking actually has a trailer.
	if (!towOnInfo) {
		return <></>;
	}

	// A characteristic flagged "Link trailer/vehicle" carries between the vehicle and its trailer,
	// so it shows on both sides — merge the vehicle's linked characteristics in here (only when a
	// vehicle actually exists, so a linked trailer characteristic doesn't show a phantom tag).
	const hasVehicle = isNotNullOrUndefined(booking.bookedSummary?.cargoInfo);
	const trailerEffectiveCharacteristics = withLinkedCharacteristics(
		booking.bookedSummary?.trailerEffectiveCharacteristics,
		hasVehicle ? booking.bookedSummary?.effectiveCharacteristics : [],
		store.cargoCharacteristicMap,
	);

	const displayUnit = appConfigStore.weightDisplayUnit;

	// Staff+ see the weight whenever the master switch is on (even if the type lacks the
	// required characteristic); customers only see it when the tow-on type's characteristics
	// require an exact weight. Exact weight when set, otherwise the selected weight band label.
	const trailerCharacteristicIds = store.towOnTypeCharacteristicsMap?.[towOnInfo.towOnType?.id ?? ''] ?? [];
	// Also show the weight when the trailer carries additional weight (a band above the smallest/base
	// weight band the breakdown prices from), so it's shown even without exact-weight input.
	const minTrailerWeightValue = findAllMeasurementsForFerryTrip('WEIGHT', 'trailer')?.[0]?.value ?? 0;
	const trailerHasAdditionalWeight =
		stringNotEmpty(towOnInfo.selectedWeightId) &&
		getMeasurementValueFromId(towOnInfo.selectedWeightId, true) > minTrailerWeightValue;
	const showTrailerWeight =
		trailerHasAdditionalWeight ||
		appConfigStore.towOnTypeRequiresExactWeight(trailerCharacteristicIds) ||
		(store.isStaff && appConfigStore.trailerExactWeightInputEnabled);
	let trailerWeightDisplay = '';
	if (showTrailerWeight) {
		if (towOnInfo.exactWeight != null && stringNotEmpty(towOnInfo.exactWeightUnit)) {
			trailerWeightDisplay = formatWeight(
				Number(towOnInfo.exactWeight),
				parseWeightUnit(towOnInfo.exactWeightUnit, displayUnit),
				displayUnit,
			);
		} else {
			// No exact weight entered — show the selected band's weight value converted to the
			// display unit (rather than the band's label).
			trailerWeightDisplay = formatWeight(
				getMeasurementValueFromId(towOnInfo.selectedWeightId ?? '', true),
				'kg',
				displayUnit,
			);
		}
	}

	const trailerLengthLabel = getMeasurementLabelFromId(towOnInfo.selectedLengthId);
	// When the vehicle and trailer widths are linked the trailer follows the vehicle's width, so
	// fall back to the vehicle's width when the trailer has none of its own (show it for both).
	const trailerWidthLabel = getWidthLabelFromId(
		appConfigStore.vehicleAndTrailerWidthLinked
			? (towOnInfo.selectedWidthId ?? booking.bookedSummary?.cargoInfo?.selectedWidthId)
			: towOnInfo.selectedWidthId,
	);

	const sectionContent = (
		<>
			<p className="trailer-info">
				{towOnInfo.towOnType.label}
				<CargoCharacteristicTags
					effectiveCharacteristics={trailerEffectiveCharacteristics}
					cargoCharacteristicMap={store.cargoCharacteristicMap}
					respectVisibility
				/>
			</p>
			<DetailLine label="Length" value={trailerLengthLabel} />
			<DetailLine label="Weight" value={trailerWeightDisplay} />
			<DetailLine label="Width" value={trailerWidthLabel} />
		</>
	);

	return (
		<BookingDetailSection
			sectionName={intl.formatMessage({
				id: 'vehicle-booking-details__trailer-details-section__label',
				defaultMessage: 'Trailer details',
			})}
			sectionContent={sectionContent}
			sectionClassName="trailer-details"
			icon={{
				name: 'rv_hookup',
				useMaterialIcons: true,
			}}
			modificationRules={{
				canEdit: canEdit,
				// The trailer is edited as part of the vehicle booking flow (the wizard's vehicle
				// tab includes the trailer), so editing this section opens the vehicle edit.
				editType: 'vehicle',
				onEdit: onEdit,
				isInvoiced: isInvoiced,
			}}
		/>
	);
}
